import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type ProjetPartage, type Succession, type TitulaireResume } from './api';
/**
 * ⚠️ LES MENUS LISENT LE VOCABULAIRE PARTAGÉ, ILS N'EN RECOPIENT PAS UN.
 * Les qualités successorales, les sources et les catégories de la masse
 * vivaient en TROIS exemplaires — la contrainte en base, le `z.enum` du
 * service, et les menus d'écran. Un menu qui propose une valeur que la base
 * refuse rend 400 sur un champ que l'agent vient de choisir dans une liste
 * déroulante : le refus accuse l'agent d'une faute qu'il n'a pas pu commettre.
 */
import {
  CATEGORIES_MASSE, LIBELLE_CATEGORIE_MASSE, LIBELLE_OPTION, LIBELLE_QUALITE,
  LIBELLE_SOURCE_DECES, LIBELLE_SOURCE_DEVOLUTION, LIBELLE_SOURCE_VALEUR,
  QUALITES_SUCCESSORALES, SOURCES_DECES, SOURCES_DEVOLUTION, SOURCES_VALEUR,
} from '../../src/partage/vocabulaire-succession.js';

/**
 * LES SUCCESSIONS — §17 · §50 · §51 · §52 · §53.
 *
 * ⚠️⚠️ CET ÉCRAN N'EXISTAIT PAS ALORS QUE LE SERVICE ÉTAIT COMPLET. Mesuré le
 * 21/09/2026 par `tests/cahier-mutation.test.ts` : `/successions` porte HUIT
 * routes — ouverture, ayants droit, option, vocation, masse, projet de
 * partage — et `api.ts` n'en citait AUCUNE. Quatre écrans du cahier étaient
 * jouables en HTTP et injouables par un agent. C'est la règle écrite dans
 * `dossier.tsx` : « un acte que seul un ingénieur peut poser n'est pas un acte
 * de l'administration ».
 *
 * ⚠️ AUCUNE RÈGLE MÉTIER N'EST RECOPIÉE ICI. La vocation, les empêchements au
 * partage, la lecture de la liquidation et l'option viennent du service. Un
 * écran qui recalculerait « la succession est-elle complète » créerait la
 * seconde table des mêmes règles que ce dépôt paie partout — et sur des
 * fractions, il la paierait en virgule flottante : trois héritiers à 1/3 font
 * 0,999, et l'écran annoncerait un millième manquant sur une succession close.
 */
const ETIQUETTE_OPTION: Record<string, string> = {
  acceptee: 'vert', acceptee_a_concurrence: 'bleu', renoncee: 'gris', indeterminee: 'ambre',
};

/**
 * LA FORME QUE PREND UN STATUT DE SUCCESSION — une seule table, deux lecteurs.
 *
 * ⚠️⚠️ ÉCRITE LE 21/09/2026 PARCE QUE `.etat` A CHANGÉ DE NATURE. La refonte de
 * `design-os-cadastral` lui a donné un `::before` qui DESSINE l'état — disque,
 * triangle, losange, carré, anneau, barre selon la teinte. Ce qui n'était qu'une
 * couleur est devenu une FORME, et une forme se lit même quand la couleur ne se
 * distingue pas : à l'impression, sur un écran délavé, pour un daltonien.
 *
 * ⚠️ ET C'EST CE CHANGEMENT QUI A RENDU VISIBLE UN DÉFAUT QUE LA COULEUR
 * CACHAIT. La fiche affichait `etat ambre` EN DUR, quel que soit le statut : une
 * succession close s'y serait annoncée « attention » — un triangle — à côté de
 * la même succession marquée d'un disque dans la liste. Deux écrans, deux
 * verdicts sur le même objet, et aucun typecheck ne pouvait le voir.
 *
 * ⚠️ LES CINQ VALEURS SONT CELLES DE `succession_statut_check`, pas des états
 * inventés. Les ONGLETS, eux, restent DEUX — seuls `ouverte` et `close` sont
 * atteignables aujourd'hui, et un onglet toujours vide se lit « le module ne
 * marche pas ». Un badge doit rendre ce que la base contient ; un onglet promet
 * qu'on peut y arriver. Ce n'est pas la même promesse.
 */
const FORME_STATUT: Record<string, string> = {
  ouverte: 'ambre',
  liquidation: 'bleu',
  partagee: 'bleu',
  close: 'vert',
  /** ⚠️ Une succession CONTESTÉE n'est pas « en cours » : c'est un litige. */
  contestee: 'rouge',
};

/** ⚠️ Un statut inconnu ne se peint PAS comme un statut connu : il se dit gris. */
const formeStatut = (statut: unknown) => FORME_STATUT[String(statut)] ?? 'gris';

/** ⚠️ EN FRACTION, JAMAIS EN POURCENTAGE — voir l'en-tête. */
const fraction = (n: number | null, d: number | null) =>
  n === null || d === null ? '—' : `${n}/${d}`;

/** Un champ de formulaire, aux classes déjà déclarées par la feuille de style. */
function Champ({ label, aide, obligatoire, children }: {
  label: string; aide?: string; obligatoire?: boolean; children: React.ReactNode;
}) {
  return (
    <label className="champ">
      <span className="champ-label">
        {label}{obligatoire && <b className="requis" aria-label="obligatoire">*</b>}
      </span>
      {children}
      {aide && <span className="champ-aide">{aide}</span>}
    </label>
  );
}

/**
 * CHERCHER UNE PERSONNE AU REGISTRE, PAR SON NOM.
 *
 * ⚠️⚠️ UN AGENT CONNAÎT UN NOM, LE SERVICE ATTEND UN IDENTIFIANT. `POST
 * /successions` et `POST /successions/:ref/ayants-droit` exigent tous deux un
 * UUID de titulaire. Sans ce composant, l'écran d'ouverture aurait demandé
 * « identifiant du défunt » — c'est-à-dire aurait été injouable par la seule
 * personne à qui il s'adresse. C'est la forme la plus discrète du « jouable en
 * HTTP, injouable par un agent » que cet écran existe pour corriger.
 *
 * ⚠️ `droits_actifs` EST AFFICHÉ, ET C'EST LUI QUI DÉPARTAGE LES HOMONYMES. Le
 * foncier guinéen en est plein : « DIALLO Mamadou » peut renvoyer six fiches.
 * Celle qui porte des biens est celle dont on ouvre la succession — un nom seul
 * ferait choisir au hasard, et le mauvais choix radierait les droits d'un
 * vivant. C'est pour cela qu'on lit `foncier.chercherTitulaires` et non le
 * doublon plus pauvre retiré d'`api.ts` le 21/09.
 *
 * ⚠️ LA RECHERCHE EST JOURNALISÉE PAR LE SERVICE, Y COMPRIS QUAND ELLE NE REND
 * RIEN. Ce n'est pas une lecture anodine : on interroge le patrimoine de
 * personnes nommées. L'écran ne le cache pas — il le DIT, pour que l'agent
 * sache que son geste laisse une trace.
 */
function ChercheurDePersonne({ libelle, aide, onChoisir }: {
  libelle: string; aide: string;
  onChoisir: (t: TitulaireResume) => void;
}) {
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState<TitulaireResume[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  async function chercher() {
    setErreur(null); setMessage(null); setOccupe(true);
    try {
      const r = await api.foncier.chercherTitulaires(q.trim());
      setResultats(r.titulaires ?? []);
      setMessage(r.message ?? null);
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "La recherche nominative exige un accès aux données personnelles (sensibilité 2) : votre rôle ne la permet pas."
        : String((e as Error).message ?? e));
      setResultats(null);
    } finally { setOccupe(false); }
  }

  return (
    <fieldset className="sous-cadre">
      <legend>{libelle}</legend>
      <p className="cadre-aide">{aide}</p>
      <div className="ligne-champs">
        <Champ label="Nom recherché" aide="deux caractères au minimum — la recherche est journalisée, y compris sans résultat">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void chercher(); } }} />
        </Champ>
        <button type="button" className="bouton discret" disabled={occupe || q.trim().length < 2}
                onClick={() => void chercher()}>Chercher</button>
      </div>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {/**
        * ⚠️ « AUCUN RÉSULTAT » ET « LE REGISTRE EST VIDE » NE SE CONFONDENT PAS,
        * et c'est le SERVICE qui les sépare : il rend le total du registre dans
        * son message. L'écran le répète tel quel plutôt que d'écrire « aucun
        * résultat », qui ferait conclure à une panne de recherche.
        */}
      {message && <p className="note-indispo">{message}</p>}

      {resultats && resultats.length > 0 && (
        <table className="tableau">
          <thead><tr><th>Personne</th><th>Type</th><th>Biens inscrits</th><th>NIN</th><th /></tr></thead>
          <tbody>
            {resultats.map((t) => (
              <tr key={t.id}>
                <td>{t.denomination}</td>
                <td>{t.type}</td>
                {/**
                  * ⚠️ LE NOMBRE DE BIENS EST LA COLONNE UTILE — voir l'en-tête.
                  * Zéro n'est pas disqualifiant (on peut hériter d'un homme qui
                  * ne possédait rien au registre), mais il DOIT se voir.
                  */}
                <td><span className="jeton">{t.droits_actifs}</span></td>
                <td>{t.nin_rattache ? 'rattaché' : '—'}</td>
                <td>
                  <button type="button" className="bouton discret"
                          onClick={() => onChoisir(t)}>Choisir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </fieldset>
  );
}

/**
 * OUVRIR UNE SUCCESSION — §17 · §50.
 *
 * ⚠️⚠️ `POST /successions` EXISTAIT, `api.successions.ouvrir` ÉTAIT CÂBLÉE, ET
 * AUCUN ÉCRAN NE L'APPELAIT. Mesuré le 21/09/2026 : quatre routes du module —
 * ouvrir, inscrire un ayant droit, réviser une vocation, inscrire à la masse —
 * avaient zéro appelant dans `frontend/src`. Le §50 du cahier était compté
 * « servi » parce que la route existe : c'est exactement ce que
 * `cahier-mutation.test.ts` avertit de ne pas conclure — l'existence d'une
 * route ne dit rien de sa complétude, et la complétude se prouve en JOUANT.
 *
 * ⚠️ AUCUNE SOURCE N'EST PRÉ-COCHÉE, ET C'EST UNE RÈGLE, PAS UN OUBLI D'UX.
 * Pré-sélectionner « acte de décès » ferait porter au registre qu'un acte
 * existe quand personne ne l'a vu — et c'est sur cette mention qu'un tiers
 * s'appuiera dans dix ans pour dire que la mort était établie. Le menu s'ouvre
 * donc sur un choix vide, et le bouton reste fermé tant qu'il l'est.
 *
 * ⚠️ LA CONSÉQUENCE DE LA DATE EST DITE AVANT LA SAISIE, PAS APRÈS. C'est elle
 * qui fixera le point de départ des droits des héritiers : entre le décès et le
 * partage il s'écoule des mois, et le service le rappelle encore dans sa
 * réponse. Un avertissement placé après le bouton ne prévient plus, il commente.
 */
function OuvrirSuccession({ onOuverte, onFerme }: {
  onOuverte: (reference: string) => void; onFerme: () => void;
}) {
  const [defunt, setDefunt] = useState<TitulaireResume | null>(null);
  const [dateDeces, setDateDeces] = useState('');
  const [lieuDeces, setLieuDeces] = useState('');
  /** ⚠️ Vide au départ : voir l'en-tête. */
  const [source, setSource] = useState('');
  const [dossier, setDossier] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const aujourdHui = new Date().toISOString().slice(0, 10);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    if (!defunt || !source) return;
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.successions.ouvrir({
        defuntTitulaireId: defunt.id,
        dateDeces,
        lieuDeces: lieuDeces.trim() || undefined,
        source,
        dossierReference: dossier.trim() || undefined,
      });
      const ref = r?.succession?.reference;
      if (typeof ref !== 'string') {
        throw new Error("Le service n'a pas rendu de référence de succession.");
      }
      onOuverte(ref);
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String((err as Error).message ?? err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire large" onSubmit={soumettre}>
      <header className="entete-scene">
        <h3>Ouvrir une succession</h3>
        <div className="actions">
          <button type="button" className="bouton discret" onClick={onFerme}>Fermer</button>
        </div>
      </header>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ⚠️ LA CONSÉQUENCE AVANT LA SAISIE — voir l'en-tête. */}
      <p className="note-indispo">
        Les droits des héritiers seront inscrits comme valables <strong>au jour du décès</strong>,
        et non au jour du partage. Entre les deux, le fonds n'est jamais sans titulaire — c'est
        cette date-ci qui le garantit.
      </p>

      {defunt ? (
        <div className="avertissement">
          <strong>Défunt : {defunt.denomination}</strong>{' '}
          — {defunt.droits_actifs} bien(s) inscrit(s) à son nom au registre.{' '}
          <button type="button" className="bouton discret" onClick={() => setDefunt(null)}>Changer</button>
        </div>
      ) : (
        <ChercheurDePersonne
          libelle="Le défunt, tel qu'il est inscrit au registre"
          aide="Cherchez par nom. La personne doit déjà figurer au registre : on n'ouvre pas la succession de quelqu'un que le foncier ne connaît pas — il n'y aurait rien à transmettre par ce registre."
          onChoisir={setDefunt} />
      )}

      <div className="ligne-champs">
        <Champ label="Date du décès" obligatoire
               aide="elle fixe le point de départ des droits des héritiers">
          {/**
            * ⚠️ `max` EST POSÉ SUR LE CHAMP, et le service refuse quand même
            * une date future — les deux sont nécessaires. Le navigateur épargne
            * un aller-retour ; lui seul ne protège rien, puisque l'appel HTTP se
            * passe de l'écran.
            */}
          <input type="date" required max={aujourdHui}
                 value={dateDeces} onChange={(e) => setDateDeces(e.target.value)} />
        </Champ>
        <Champ label="Lieu du décès" aide="c'est là qu'on ira chercher l'acte de décès">
          <input value={lieuDeces} onChange={(e) => setLieuDeces(e.target.value)} />
        </Champ>
      </div>

      <Champ label="Le décès est établi par" obligatoire
             aide="aucune valeur n'est proposée par défaut : dire « acte de décès » fait porter au registre qu'un acte existe">
        <select required value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">— choisir —</option>
          {SOURCES_DECES.map((v) => (
            <option key={v} value={v}>{LIBELLE_SOURCE_DECES[v]}</option>
          ))}
        </select>
      </Champ>

      {/**
        * ⚠️ LE DOSSIER N'EST PAS DÉCORATIF. C'est lui qui relie la succession à
        * la demande déposée au guichet — et `documents.ts` retrouve le
        * formulaire d'un acte par `mutation → dossier → demande de même type`.
        * Sans ce rattachement, l'acte de dévolution s'imprime sans état civil du
        * défunt : le guichet a été traversé, et rien de ce qu'il a recueilli
        * n'atteint le papier.
        */}
      <Champ label="Dossier ouvert au guichet"
             aide="facultatif, mais sans lui l'acte s'imprimera sans l'état civil recueilli au guichet (ex. GN-DOS-000000012)">
        <input value={dossier} onChange={(e) => setDossier(e.target.value)} placeholder="GN-DOS-…" />
      </Champ>

      <div className="actions">
        <button type="submit" className="bouton" disabled={envoi || !defunt || !source || !dateDeces}>
          {envoi ? 'Ouverture…' : 'Ouvrir la succession'}
        </button>
      </div>
    </form>
  );
}

export function Successions({ ouvrir }: {
  /**
   * LA SUCCESSION À OUVRIR EN ARRIVANT, quand on vient du cockpit.
   *
   * ⚠️ MÊME MOTIF QUE `Mutations({ ouvrir })`, ET POUR LA MÊME RAISON : sans
   * cette entrée, le cockpit ne peut que déposer l'agent sur la LISTE, et le
   * dossier qu'il venait de montrer est à retrouver à la main. Un cockpit qui
   * fait chercher ce qu'il vient d'afficher n'a pas rendu service.
   *
   * ⚠️ ET C'EST UNE ENTRÉE, PAS UNE VALEUR INITIALE : deux clics successifs sur
   * deux successions différentes ne remontent pas l'écran. Sans l'effet
   * ci-dessous, le second clic n'ouvrirait rien — défaut déjà payé sur
   * `Mutations`, consigné dans son propre commentaire.
   */
  ouvrir?: string | null;
} = {}) {
  const [liste, setListe] = useState<any[]>([]);
  const [ouverte, setOuverte] = useState<string | null>(ouvrir ?? null);
  /**
   * §5 du cahier de navigation — le NIVEAU 2 de cet écran.
   *
   * ⚠️ DEUX ÉTATS SEULEMENT, ET ILS SONT MESURÉS, PAS INVENTÉS. Le code
   * n'emploie que `close` ; tout le reste est ouvert (`ouverte` en base). Poser
   * « En cours », « Bloquées » ou « À signer » parce que le cahier les
   * mentionne fabriquerait des onglets qu'aucune donnée ne peut remplir — et
   * un onglet toujours vide se lit « le module ne marche pas ».
   */
  const [statut, setStatut] = useState<string>('');
  const [comptes, setComptes] = useState<Record<string, number>>({});
  const [restreint, setRestreint] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvreUne, setOuvreUne] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await api.successions.liste(statut || undefined);
      setListe(r.successions ?? []);
      setComptes(r.comptes ?? {});
      setRestreint(r.lecture ?? null);
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter les successions (fon.succession:lire)."
        : String((e as Error).message ?? e));
    }
  }, [statut]);

  useEffect(() => { void charger(); }, [charger]);
  useEffect(() => { if (ouvrir) setOuverte(ouvrir); }, [ouvrir]);

  if (ouverte) {
    return <FicheSuccession reference={ouverte} onFerme={() => { setOuverte(null); void charger(); }} />;
  }

  /**
   * ⚠️ ON OUVRE DIRECTEMENT SUR LA FICHE APRÈS CRÉATION. Reposer l'agent sur la
   * liste l'obligerait à retrouver la succession qu'il vient de créer — et la
   * suite du travail (inscrire les ayants droit) est TOUJOURS la sienne. Même
   * motif que l'entrée `ouvrir` du cockpit, commentée plus haut.
   */
  if (ouvreUne) {
    return <OuvrirSuccession onFerme={() => setOuvreUne(false)}
      onOuverte={(ref) => { setOuvreUne(false); setOuverte(ref); }} />;
  }

  return (
    <div className="scene-liste">
      <header className="entete-scene">
        <h3>Successions</h3>
        <div className="actions">
          {/**
            * ⚠️⚠️ CE BOUTON MANQUAIT, ET C'EST LE PREMIER MAILLON DE LA CHAÎNE.
            * `POST /successions` existait, `api.successions.ouvrir` était
            * câblée — et rien ne l'appelait : une succession ne pouvait être
            * ouverte que par un appel HTTP direct. Un acte que seul un
            * ingénieur peut poser n'est pas un acte de l'administration.
            */}
          <button type="button" className="bouton" onClick={() => setOuvreUne(true)}>
            Ouvrir une succession
          </button>
        </div>
      </header>

      <nav className="onglets">
        <button type="button" className={`bouton ${statut === '' ? 'actif' : 'discret'}`}
          onClick={() => setStatut('')}>
          Toutes <span className="jeton">{Object.values(comptes).reduce((a, b) => a + b, 0)}</span>
        </button>
        {['ouverte', 'close'].map((st) => (
          <button key={st} type="button" className={`bouton ${statut === st ? 'actif' : 'discret'}`}
            onClick={() => setStatut(st)}>
            {st === 'ouverte' ? 'Ouvertes' : 'Closes'} <span className="jeton">{comptes[st] ?? 0}</span>
          </button>
        ))}
      </nav>

      {/* ⚠️ CE QUI EST RESTREINT SE DIT — le service seul sait ce qu'il a filtré. */}
      {restreint && <p className="note-indispo">{restreint}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {liste.length === 0 && !erreur ? (
        /**
         * ⚠️ « AUCUNE SUCCESSION » N'EST PAS « LE MODULE NE MARCHE PAS », et
         * les deux se ressemblent trait pour trait sur un écran vide. On dit
         * lequel des deux c'est.
         */
        <p className="vide">
          Aucune succession ouverte au registre. Ce n'est pas une panne : la liste est vide
          tant qu'aucun décès n'a été déclaré au service foncier.
        </p>
      ) : (
        <table className="tableau">
          <thead>
            <tr><th>Référence</th><th>Défunt</th><th>Décès</th><th>Statut</th><th /></tr>
          </thead>
          <tbody>
            {liste.map((s) => (
              <tr key={s.reference}>
                <td><code>{s.reference}</code></td>
                <td>{s.defunt}</td>
                <td>{s.date_deces ?? '—'}</td>
                <td><span className={`etat ${formeStatut(s.statut)}`}>{s.statut}</span></td>
                <td>
                  <button className="bouton discret" onClick={() => setOuverte(s.reference)}>Ouvrir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FicheSuccession({ reference, onFerme }: { reference: string; onFerme: () => void }) {
  const [s, setS] = useState<Succession | null>(null);
  const [partage, setPartage] = useState<ProjetPartage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [depose, setDepose] = useState<string[]>([]);
  /** Les trois formulaires de la fiche — un seul ouvert à la fois par section. */
  const [inscritAyant, setInscritAyant] = useState(false);
  const [revise, setRevise] = useState<string | null>(null);
  const [inscritMasse, setInscritMasse] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setS(await api.successions.fiche(reference)); }
    catch (e) { setErreur(String((e as Error).message ?? e)); }
  }, [reference]);

  useEffect(() => { void charger(); }, [charger]);

  async function opter(id: string, option: string) {
    setMessage(null); setErreur(null); setOccupe(true);
    try {
      await api.successions.opter(reference, id, { option });
      setMessage(`Option enregistrée : ${LIBELLE_OPTION[option] ?? option}.`);
      await charger();
      // Le projet de partage dépend des options : il cesse d'être vrai.
      setPartage(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e));
    } finally { setOccupe(false); }
  }

  async function composerPartage() {
    setMessage(null); setErreur(null); setOccupe(true);
    try { setPartage(await api.successions.projetPartage(reference)); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e)); }
    finally { setOccupe(false); }
  }

  /**
   * DÉPOSER LA DÉVOLUTION — §50 · §53.
   *
   * ⚠️⚠️ EN TYPE `succession`, ET PAS `partage` : la mesure a corrigé deux
   * passations. `/projet-partage` compose des quotités INDIVISES avec
   * `valideDu = date_deces` — c'est une DÉVOLUTION, que le type `succession`
   * porte déjà, applicable et éprouvée. Les deux passations écrivaient « il ne
   * manque que le type de mutation `partage` pour le déposer » ; il ne manquait
   * pas un type, il manquait CE BOUTON. Le vrai partage — sortie d'indivision,
   * attribution de lots, soultes — reste une autre opération.
   *
   * ⚠️ UN DÉPÔT PAR FONDS. `fon.mutation` porte UNE parcelle : une succession
   * sur trois immeubles fait trois actes, comme l'échange en fait deux. Les
   * fondre serait mentir sur ce que le registre sait représenter.
   *
   * ⚠️ ON NE TRANSMET QUE LES CHAMPS DU SCHÉMA. `projets[].droits[]` porte
   * aussi `denomination`, qui n'est qu'un libellé d'affichage : l'envoyer
   * serait inoffensif AUJOURD'HUI — zod le jetterait — mais c'est exactement
   * ce silence qui a coûté le §17 ce soir, où `valideDu` était jeté de la même
   * façon sans que rien ne le dise. On envoie ce que le contrat accepte.
   */
  async function deposer(projet: Record<string, any>) {
    setMessage(null); setErreur(null); setOccupe(true);
    try {
      const r = await api.mutations.deposer({
        type: 'succession',
        identifiantParcelle: String(projet.parcelle),
        /**
         * ⚠️⚠️ LE DOSSIER EST TRANSMIS QUAND LA SUCCESSION EN PORTE UN, ET
         * C'EST CE QUI FAIT TENIR LA CHAÎNE. `documents.ts` retrouve le
         * formulaire d'un acte par `mutation → dossier → demande de MÊME
         * type` : sans ce rattachement, l'acte de dévolution s'imprime sans
         * aucun état civil du défunt — le guichet a été traversé, et rien de
         * ce qu'il a recueilli n'atteint le papier. Aucune erreur n'est levée,
         * et le papier paraît complet : les cadres disent « champ non
         * renseigné au guichet », ce qui est faux.
         */
        ...(s?.succession.affaire ? { dossierReference: String(s.succession.affaire) } : {}),
        effetProjete: {
          radier: projet.radier,
          droits: (projet.droits as any[]).map((d) => ({
            titulaire: d.titulaire, nature: d.nature,
            quotiteNum: d.quotiteNum, quotiteDen: d.quotiteDen,
            valideDu: d.valideDu,
          })),
        },
        motif: `Dévolution successorale ${reference} — effet au jour du décès.`,
      } as any);
      const ref = (r as any)?.mutation?.reference ?? '(référence non rendue)';
      setDepose((v) => [...v, String(projet.parcelle)]);
      setMessage(`Acte déposé sur ${projet.parcelle} : ${ref}. Il reste à instruire et à valider — `
        + `le registre n'a pas encore changé.`);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e));
    } finally { setOccupe(false); }
  }

  if (erreur && !s) return (
    <div className="fiche-formulaire large">
      <p className="erreur" role="alert">{erreur}</p>
      <button className="bouton discret" onClick={onFerme}>Fermer</button>
    </div>
  );
  if (!s) return <div className="attente-plein">Chargement de la succession {reference}…</div>;

  const vivants = s.ayantsDroit.filter((a) => !a.ecarte_le);
  const ecartes = s.ayantsDroit.filter((a) => a.ecarte_le);

  return (
    <div className="fiche-formulaire large dossier">
      <header className="entete-scene">
        <h3>Succession <code>{s.succession.reference}</code> — {s.succession.defunt}</h3>
        <div className="actions">
          <span className={`etat ${formeStatut(s.succession.statut)}`}>{String(s.succession.statut)}</span>
          <button className="bouton discret" onClick={onFerme}>Fermer</button>
        </div>
      </header>

      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ─── §17 — « le mort saisit le vif » : la date d'effet des droits ─── */}
      <p className="note-indispo">
        Les droits des héritiers prennent effet au <strong>{s.succession.date_deces ?? '—'}</strong>,
        jour du décès — et non au jour du partage. Entre les deux, le fonds n'est jamais sans titulaire.
      </p>

      {/* ─── §52 — la liquidation ─── */}
      <section>
        <h4>Liquidation</h4>
        <div className="chiffres">
          <div className="chiffre">
            <span className="chiffre-libelle">Actif</span>
            <strong className="chiffre-valeur">{s.liquidation.actif.toLocaleString('fr-FR')}</strong>
          </div>
          <div className="chiffre">
            <span className="chiffre-libelle">Passif</span>
            <strong className="chiffre-valeur">{s.liquidation.passif.toLocaleString('fr-FR')}</strong>
          </div>
          <div className="chiffre">
            <span className="chiffre-libelle">Net</span>
            <strong className="chiffre-valeur">{s.liquidation.net.toLocaleString('fr-FR')}</strong>
          </div>
        </div>
        {/**
          * ⚠️ LA LECTURE VIENT DU SERVICE, et elle porte le cas qui coûte : une
          * succession DÉFICITAIRE. Accepter purement et simplement y fait
          * supporter la dette. L'avertissement est AU-DESSUS des boutons
          * d'option, jamais en dessous — après, il ne prévient plus, il
          * commente.
          */}
        {s.liquidation.lecture && (
          <div className={`avertissement ${s.liquidation.net < 0 ? 'danger' : ''}`}>
            {s.liquidation.lecture}
          </div>
        )}
      </section>

      {/* ─── §51 — les ayants droit ─── */}
      <section>
        <h4>Ayants droit</h4>
        <div className="actions">
          {/**
            * ⚠️⚠️ SANS CE BOUTON, LA SUCCESSION S'OUVRAIT ET S'ARRÊTAIT LÀ.
            * `POST /successions/:ref/ayants-droit` n'avait aucun appelant :
            * une succession ouverte à l'écran ne pouvait recevoir aucun
            * héritier, donc aucune vocation, donc aucun projet de partage. La
            * chaîne était rompue au DEUXIÈME maillon, et l'écran ne le disait
            * pas — il affichait « Aucun ayant droit inscrit », ce qui est vrai
            * et se lit comme un état des données, jamais comme un manque de
            * l'écran.
            */}
          <button type="button" className="bouton" disabled={String(s.succession.statut) === 'close'}
                  onClick={() => setInscritAyant((v) => !v)}>
            {inscritAyant ? "Fermer" : "Inscrire un ayant droit"}
          </button>
        </div>

        {inscritAyant && (
          <InscrireAyantDroit reference={reference}
            onFait={(m) => { setInscritAyant(false); setMessage(m); setPartage(null); void charger(); }} />
        )}
        {vivants.length === 0 ? (
          <p className="vide">
            Aucun ayant droit inscrit. Tant que la dévolution n'est pas établie, aucun partage
            ne peut être composé — ce n'est pas un défaut d'affichage.
          </p>
        ) : (
          <table className="tableau">
            <thead>
              <tr><th>Personne</th><th>Qualité</th><th>Vocation</th><th>Option</th><th>Source</th><th /></tr>
            </thead>
            <tbody>
              {vivants.map((a) => (
                <tr key={a.id}>
                  <td>{a.denomination}{a.lien ? <span className="aide"> · {a.lien}</span> : null}</td>
                  <td>{a.qualite}</td>
                  <td><span className="fraction">{fraction(a.vocation_num, a.vocation_den)}</span></td>
                  <td>
                    <span className={`etat ${ETIQUETTE_OPTION[a.option] ?? 'gris'}`}>
                      {LIBELLE_OPTION[a.option] ?? a.option}
                    </span>
                  </td>
                  <td>{a.source}</td>
                  <td>
                    {/**
                      * ⚠️ L'OPTION PAR DÉFAUT EST « NON EXERCÉE », JAMAIS
                      * « ACCEPTÉE » — le défaut inverse ferait du silence une
                      * acceptation, donc supporter le passif à qui ne l'a pas
                      * voulu. L'écran n'offre donc aucun geste par omission :
                      * il faut cliquer.
                      */}
                    <div className="actions-ligne">
                      {a.option === 'indeterminee' ? (
                        <>
                          <button className="bouton discret" disabled={occupe}
                            onClick={() => void opter(a.id, 'acceptee')}>Accepter</button>
                          <button className="bouton discret" disabled={occupe}
                            onClick={() => void opter(a.id, 'acceptee_a_concurrence')}>À concurrence</button>
                          <button className="bouton discret" disabled={occupe}
                            onClick={() => void opter(a.id, 'renoncee')}>Renoncer</button>
                        </>
                      ) : (
                        <span className="aide">exercée le {a.option_le ?? '—'}</span>
                      )}
                      {/**
                        * ⚠️⚠️ L'ACCROISSEMENT — ET SANS CE BOUTON, UNE
                        * RENONCIATION REND LA SUCCESSION DÉFINITIVEMENT
                        * IMPARTAGEABLE. La route existe
                        * (`…/ayants-droit/:id/vocation`) et n'avait aucun
                        * appelant : après qu'un héritier a renoncé, les parts
                        * acceptées ne couvrent plus le tout, le projet de
                        * partage refuse — et aucun chemin ne permettait de les
                        * rétablir. Le défaut ne se voit pas en lisant le
                        * module ; il se voit en JOUANT la renonciation.
                        */}
                      <button className="bouton discret" disabled={occupe}
                        onClick={() => setRevise(revise === a.id ? null : a.id)}>
                        {revise === a.id ? 'Annuler' : 'Réviser la part'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {revise && (
          <ReviserVocation reference={reference} ayantDroitId={revise}
            vivant={vivants.find((a) => a.id === revise)}
            onFait={(m) => { setRevise(null); setMessage(m); setPartage(null); void charger(); }} />
        )}

        {/**
          * ⚠️ LES ÉCARTÉS SONT MONTRÉS, jamais retirés de la vue. Un héritier
          * écarté l'a été POUR UN MOTIF, et ce motif se conteste : le faire
          * disparaître de l'écran ferait disparaître la contestation avec lui.
          */}
        {ecartes.length > 0 && (
          <>
            <h5 className="proj-tete">Écartés de la dévolution</h5>
            <ul className="liste-fine">
              {ecartes.map((a) => (
                <li key={a.id}>
                  {a.denomination} — {a.motif_ecart ?? 'motif non consigné'} (le {a.ecarte_le})
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* ─── §52 — ce que le REGISTRE constate, face à ce qui est déclaré ─── */}
      <section>
        <h4>Fonds inscrits au défunt</h4>
        {/**
          * ⚠️ DEUX LISTES QUI NE DISENT PAS LA MÊME CHOSE, et c'est tout l'objet
          * de la liquidation : la masse est DÉCLARÉE, ces fonds-ci sont
          * CONSTATÉS au registre — donc opposables. Les fondre en une seule
          * ferait passer une déclaration pour un droit inscrit.
          */}
        {s.fondsInscritsAuDefunt.length === 0 ? (
          <p className="vide">
            Le registre ne porte aucun droit actif au nom du défunt. Ce n'est pas « il ne
            possédait rien » : c'est « rien n'est inscrit à son nom ici » — un bien non
            immatriculé n'y figure pas.
          </p>
        ) : (
          <ul className="liste-fine">
            {s.fondsInscritsAuDefunt.map((f, k) => (
              <li key={k}>
                <code>{f.parcelle}</code> — {f.nature}{' '}
                <span className="fraction">{fraction(f.quotite_num, f.quotite_den)}</span>
              </li>
            ))}
          </ul>
        )}

        <h5 className="proj-tete">Masse déclarée</h5>
        <div className="actions">
          {/**
            * ⚠️ SANS LA MASSE, LA LIQUIDATION EST MUETTE, ET L'OPTION SE JOUE À
            * L'AVEUGLE. `POST /successions/:ref/masse` n'avait aucun appelant :
            * l'actif et le passif restaient à zéro, « net : 0 » s'affichait, et
            * l'avertissement du service sur une succession DÉFICITAIRE ne
            * pouvait jamais se déclencher. Un héritier acceptait donc purement
            * et simplement une dette que l'écran n'avait aucun moyen de
            * connaître — et c'est le geste le plus lourd de cette fiche.
            */}
          <button type="button" className="bouton discret" disabled={String(s.succession.statut) === 'close'}
                  onClick={() => setInscritMasse((v) => !v)}>
            {inscritMasse ? 'Fermer' : 'Inscrire un élément à la masse'}
          </button>
        </div>

        {inscritMasse && (
          <InscrireElementMasse reference={reference}
            onFait={(m) => { setInscritMasse(false); setMessage(m); void charger(); }} />
        )}

        {s.masse.length === 0 ? (
          <p className="vide">Aucun élément déclaré à la masse.</p>
        ) : (
          <table className="tableau">
            <thead><tr><th>Nature</th><th>Catégorie</th><th>Désignation</th><th>Valeur</th><th>Source</th></tr></thead>
            <tbody>
              {s.masse.map((m) => (
                <tr key={m.id}>
                  <td>{m.nature}</td>
                  <td>{m.categorie}</td>
                  <td>{m.designation}</td>
                  <td>{m.valeur ? `${Number(m.valeur).toLocaleString('fr-FR')} ${m.devise ?? ''}` : '—'}</td>
                  <td>{m.source_valeur ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ─── §53 — le partage ─── */}
      <section>
        <h4>Projet de partage</h4>
        <div className="actions">
          <button className="bouton" disabled={occupe} onClick={() => void composerPartage()}>
            Composer le projet de partage
          </button>
        </div>

        {partage && (
          <>
            {/**
              * ⚠️ LES EMPÊCHEMENTS SONT NOMMÉS UN PAR UN, jamais résumés en
              * « non prêt ». Un booléen dirait à l'agent qu'il ne peut pas
              * déposer, sans lui dire ce qui manque — et il chercherait la
              * cause dans son propre travail.
              */}
            {partage.empechements.length > 0 ? (
              <div className="avertissement danger">
                <strong>Le partage ne peut pas être déposé :</strong>
                <ul>{partage.empechements.map((e, k) => <li key={k}>{e}</li>)}</ul>
              </div>
            ) : (
              <div className="avertissement">
                <strong>Rien ne s'oppose au dépôt du partage.</strong>{' '}
                {partage.acceptants} acceptant(s), {partage.fondsDuDefunt.length} fonds concerné(s).
              </div>
            )}

            {partage.lecture && <p className="proj-lecture">{partage.lecture}</p>}

            {/**
              * ⚠️ LE TYPE DE MUTATION `partage` N'EXISTE PAS ENCORE — le projet
              * se compose, il ne se DÉPOSE pas. On l'écrit plutôt que d'offrir
              * un bouton qui rendrait 409 : un bouton ouvert qui refuse fait
              * chercher la panne, un manque annoncé fait attendre la suite.
              */}
            {/**
              * ⚠️ LE BOUTON N'EXISTE QUE SI LE SERVICE DIT QUE RIEN N'EMPÊCHE.
              * L'écran ne décide pas : `pretADeposer` vient de la route, au
              * même endroit que le refus. Un bouton ouvert qui rend 409 fait
              * chercher la panne dans son propre travail.
              */}
            {partage.pretADeposer && (
              <div className="actions-ligne">
                {partage.fondsDuDefunt.map((f: any) => {
                  const projet = (partage.projets as any[]).find((p) => p.parcelle === f.parcelle);
                  const fait = depose.includes(String(f.parcelle));
                  return (
                    <button key={String(f.parcelle)} className="bouton" disabled={occupe || fait || !projet}
                      onClick={() => projet && void deposer(projet)}>
                      {fait ? `Déposé sur ${f.parcelle}` : `Déposer sur ${f.parcelle}`}
                    </button>
                  );
                })}
              </div>
            )}

            <p className="note-indispo">
              Chaque fonds fait un acte : le registre rattache une mutation à UNE parcelle.
              Le dépôt n'écrit rien au registre — il enregistre une intention, qui devra être
              instruite puis validée à deux voix.
              {' '}⚠️ Ce qui est déposé ici est une <strong>dévolution</strong> : les héritiers
              reçoivent des quotités indivises, prenant effet au jour du décès. Le PARTAGE —
              sortie d'indivision, attribution de lots, soultes — est une autre opération,
              qui n'est pas encore au catalogue.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * INSCRIRE UN AYANT DROIT — §51.
 *
 * ⚠️⚠️ DEUX CHEMINS, ET IL EN FAUT DEUX. Le service exige un `titulaireId` :
 * l'héritier doit être une personne du registre. Or la moitié des héritiers
 * n'y figurent pas — on n'entre au registre foncier qu'en détenant un bien, et
 * un enfant n'en détenait aucun du vivant de son père. Un écran qui ne
 * proposerait que la recherche serait bloqué sur le cas le plus fréquent ; un
 * écran qui créerait systématiquement une fiche fabriquerait des doublons sur
 * le cas le moins fréquent — celui où l'héritier possède déjà. On cherche
 * d'abord, on crée ensuite, et jamais l'inverse.
 *
 * ⚠️ « INSCRIRE UNE PERSONNE » N'EST PAS « LUI ATTRIBUER UN DROIT ». `POST
 * /titulaires` crée une FICHE, rien de plus : aucun bien ne change de mains
 * ici. Le transfert se fera au dépôt de l'acte, après les options et la double
 * validation. Le dire évite qu'un agent croie avoir déjà transmis.
 *
 * ⚠️ L'OPTION N'EST PAS DEMANDÉE ICI. Un héritier inscrit n'est pas un héritier
 * acceptant : l'option s'exerce APRÈS connaissance du passif, et le service
 * pose `indeterminee` par défaut. Offrir « accepte » dans ce formulaire ferait
 * accepter une dette à quelqu'un qui n'en connaît pas encore le montant.
 */
function InscrireAyantDroit({ reference, onFait }: {
  reference: string; onFait: (message: string) => void;
}) {
  const [personne, setPersonne] = useState<TitulaireResume | null>(null);
  const [nouvelle, setNouvelle] = useState('');
  const [qualite, setQualite] = useState<string>('descendant');
  const [lien, setLien] = useState('');
  const [num, setNum] = useState('');
  const [den, setDen] = useState('');
  /** ⚠️ Vide au départ, même motif que la source du décès. */
  const [source, setSource] = useState('');
  const [piece, setPiece] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    if (!source) return;
    setErreur(null); setEnvoi(true);
    try {
      let titulaireId = personne?.id ?? null;
      let cree = false;
      if (!titulaireId) {
        if (nouvelle.trim().length < 2) {
          throw new Error("Choisissez une personne du registre, ou donnez le nom de celle à inscrire.");
        }
        const r = await api.titulaires.inscrire({
          type: 'personne_physique',
          denomination: nouvelle.trim(),
          motif: `Ayant droit de la succession ${reference}`,
        });
        titulaireId = r.titulaire.id;
        cree = true;
      }
      /**
       * ⚠️ ON N'ENVOIE LA VOCATION QUE SI ELLE EST SAISIE EN ENTIER. Le service
       * refuse un numérateur sans dénominateur — à juste titre : une part qu'on
       * ne sait pas lire n'est pas une part. Envoyer `NaN` produirait un 400
       * dont le message parlerait d'un champ que l'agent croit avoir rempli.
       */
      const avecPart = num.trim() !== '' && den.trim() !== '';
      await api.successions.inscrireAyantDroit(reference, {
        titulaireId, qualite,
        lien: lien.trim() || undefined,
        ...(avecPart ? { vocationNum: Number(num), vocationDen: Number(den) } : {}),
        source,
        referencePiece: piece.trim() || undefined,
      });
      onFait(`Ayant droit inscrit${cree ? ' (fiche de personne créée au registre)' : ''}. `
        + `Il n'a pas encore opté : il n'accepte ni ne renonce tant qu'il ne l'a pas déclaré.`);
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String((err as Error).message ?? err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire" onSubmit={soumettre}>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {personne ? (
        <div className="avertissement">
          <strong>{personne.denomination}</strong> — {personne.droits_actifs} bien(s) au registre.{' '}
          <button type="button" className="bouton discret" onClick={() => setPersonne(null)}>Changer</button>
        </div>
      ) : (
        <>
          <ChercheurDePersonne
            libelle="L'héritier, s'il figure déjà au registre"
            aide="Un héritier qui détenait déjà un bien y est inscrit. Sinon, laissez la recherche de côté et donnez son nom ci-dessous — on ne crée une fiche qu'après avoir cherché, pour ne pas doubler une personne existante."
            onChoisir={setPersonne} />
          <Champ label="À défaut — nom de la personne à inscrire au registre"
                 aide="inscrire n'est pas attribuer : aucun bien ne change de mains ici, une fiche est créée">
            <input value={nouvelle} onChange={(e) => setNouvelle(e.target.value)}
                   placeholder="DIALLO Mamadou Saliou" />
          </Champ>
        </>
      )}

      <div className="ligne-champs">
        <Champ label="Qualité" obligatoire aide="elle décide de la vocation, et du passif qu'elle emporte">
          <select value={qualite} onChange={(e) => setQualite(e.target.value)}>
            {QUALITES_SUCCESSORALES.map((q) => (
              <option key={q} value={q}>{LIBELLE_QUALITE[q]}</option>
            ))}
          </select>
        </Champ>
        <Champ label="Lien" aide="« fils aîné », « deuxième épouse » — ce que l'acte de notoriété dit">
          <input value={lien} onChange={(e) => setLien(e.target.value)} />
        </Champ>
      </div>

      {/**
        * ⚠️ EN FRACTION, JAMAIS EN POURCENTAGE, et les deux cases vont
        * ensemble. Trois héritiers à 1/3 font 1 ; à 33,3 % ils ne font pas le
        * tout, et le tiers manquant appartiendrait à personne.
        */}
      <div className="ligne-champs">
        <Champ label="Vocation — numérateur"
               aide="facultatif : la dévolution se fixe souvent à l'instruction, pas au moment d'inscrire">
          <input type="number" min={1} value={num} onChange={(e) => setNum(e.target.value)} />
        </Champ>
        <Champ label="Vocation — dénominateur">
          <input type="number" min={1} value={den} onChange={(e) => setDen(e.target.value)} />
        </Champ>
      </div>

      <div className="ligne-champs">
        <Champ label="La vocation est établie par" obligatoire
               aide="établir la mort et établir qui succède sont deux démonstrations distinctes">
          <select required value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">— choisir —</option>
            {SOURCES_DEVOLUTION.map((v) => (
              <option key={v} value={v}>{LIBELLE_SOURCE_DEVOLUTION[v]}</option>
            ))}
          </select>
        </Champ>
        <Champ label="Référence de la pièce" aide="numéro de l'acte de notoriété, du jugement, du testament">
          <input value={piece} onChange={(e) => setPiece(e.target.value)} />
        </Champ>
      </div>

      <div className="actions">
        <button type="submit" className="bouton" disabled={envoi || !source}>
          {envoi ? 'Inscription…' : "Inscrire l'ayant droit"}
        </button>
      </div>
    </form>
  );
}

/**
 * RÉVISER LA PART D'UN AYANT DROIT — l'accroissement.
 *
 * ⚠️⚠️ LE MOTIF EST OBLIGATOIRE, ET CE N'EST PAS DE L'ARCHIVAGE. Une part qui
 * change sans raison inscrite est indéfendable devant un héritier écarté :
 * c'est exactement ce qu'une contestation cherche. Le service l'exige (cinq
 * caractères au minimum) et le journalise ; l'écran ne peut donc pas l'omettre,
 * et il DIT pourquoi plutôt que d'afficher un champ obligatoire muet.
 *
 * ⚠️ L'ÉCRAN NE CALCULE RIEN. L'accroissement dépend du droit applicable, des
 * souches, des représentations et du testament — le registre ne les connaît
 * pas. Il enregistre ce que le notaire arrête, et il VÉRIFIE que la somme
 * retombe juste : le contrôle revient AVEC la réponse du service, au moment
 * même où l'on révise.
 */
function ReviserVocation({ reference, ayantDroitId, vivant, onFait }: {
  reference: string; ayantDroitId: string;
  vivant?: { denomination: string; vocation_num: number | null; vocation_den: number | null };
  onFait: (message: string) => void;
}) {
  const [num, setNum] = useState(String(vivant?.vocation_num ?? ''));
  const [den, setDen] = useState(String(vivant?.vocation_den ?? ''));
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.successions.fixerVocation(reference, ayantDroitId, {
        vocationNum: Number(num), vocationDen: Number(den), motif: motif.trim(),
      });
      /**
       * ⚠️ LE CONTRÔLE RENDU PAR LE SERVICE EST REMONTÉ TEL QUEL. C'est lui qui
       * sait si les parts acceptées couvrent désormais la totalité — et c'est
       * au moment de réviser qu'on doit le voir, pas sur un écran suivant.
       */
      onFait(String((r as any)?.controle ?? 'Vocation révisée.'));
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String((err as Error).message ?? err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire" onSubmit={soumettre}>
      <h5 className="proj-tete">Réviser la part de {vivant?.denomination ?? 'cet ayant droit'}</h5>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      <p className="cadre-aide">
        Une renonciation ne redistribue rien d'elle-même : les parts des autres doivent être
        rétablies ici, sans quoi elles ne couvrent plus le tout et le partage reste impossible.
      </p>
      <div className="ligne-champs">
        <Champ label="Numérateur" obligatoire>
          <input type="number" min={1} required value={num} onChange={(e) => setNum(e.target.value)} />
        </Champ>
        <Champ label="Dénominateur" obligatoire>
          <input type="number" min={1} required value={den} onChange={(e) => setDen(e.target.value)} />
        </Champ>
      </div>
      <Champ label="Motif" obligatoire
             aide="une part qui change sans raison inscrite est indéfendable devant un héritier écarté">
        <input required minLength={5} value={motif} onChange={(e) => setMotif(e.target.value)}
               placeholder="Accroissement après renonciation de …" />
      </Champ>
      <div className="actions">
        <button type="submit" className="bouton" disabled={envoi || !num || !den || motif.trim().length < 5}>
          {envoi ? 'Révision…' : 'Enregistrer la part'}
        </button>
      </div>
    </form>
  );
}

/**
 * INSCRIRE UN ÉLÉMENT À LA MASSE — §52.
 *
 * ⚠️ UNE VALEUR SANS SOURCE N'EST PAS UNE ÉVALUATION, et le service le dit dans
 * sa réponse. La fiscalité successorale s'appuie sur l'expertise ; une
 * estimation de complaisance qui ne dirait pas d'où elle vient passerait pour
 * l'une. L'écran remonte donc l'avertissement du service plutôt que d'en écrire
 * un second qui divergerait.
 *
 * ⚠️ « ACTIF » ET « PASSIF » SONT LE PREMIER CHOIX, ET LE PLUS LOURD. Un net
 * négatif n'est pas une erreur de saisie : c'est une succession déficitaire, et
 * c'est ce qui rend l'option des héritiers décisive. Saisir une dette en actif
 * ferait accepter purement et simplement une succession qu'il fallait refuser.
 */
function InscrireElementMasse({ reference, onFait }: {
  reference: string; onFait: (message: string) => void;
}) {
  const [nature, setNature] = useState<'actif' | 'passif'>('actif');
  const [categorie, setCategorie] = useState<string>('immeuble');
  const [designation, setDesignation] = useState('');
  const [valeur, setValeur] = useState('');
  const [sourceValeur, setSourceValeur] = useState('');
  const [parcelle, setParcelle] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.successions.inscrireMasse(reference, {
        nature, categorie, designation: designation.trim(),
        ...(valeur.trim() !== '' ? { valeur: Number(valeur) } : {}),
        ...(sourceValeur ? { sourceValeur } : {}),
        ...(parcelle.trim() ? { identifiantParcelle: parcelle.trim() } : {}),
      });
      // ⚠️ L'AVERTISSEMENT VIENT DU SERVICE — voir l'en-tête.
      onFait(String((r as any)?.avertissement ?? 'Élément inscrit à la masse.'));
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String((err as Error).message ?? err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire" onSubmit={soumettre}>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      <div className="ligne-champs">
        <Champ label="Nature" obligatoire aide="une dette saisie en actif ferait accepter une succession qu'il fallait refuser">
          <select value={nature} onChange={(e) => setNature(e.target.value as 'actif' | 'passif')}>
            <option value="actif">Actif — ce que le défunt laisse</option>
            <option value="passif">Passif — ce qu'il doit</option>
          </select>
        </Champ>
        <Champ label="Catégorie" obligatoire>
          <select value={categorie} onChange={(e) => setCategorie(e.target.value)}>
            {CATEGORIES_MASSE.map((c) => (
              <option key={c} value={c}>{LIBELLE_CATEGORIE_MASSE[c]}</option>
            ))}
          </select>
        </Champ>
      </div>
      <Champ label="Désignation" obligatoire aide="trois caractères au minimum — ce qu'un tiers doit pouvoir identifier">
        <input required minLength={3} value={designation} onChange={(e) => setDesignation(e.target.value)} />
      </Champ>
      <div className="ligne-champs">
        <Champ label="Valeur (GNF)" aide="facultative : une succession peut se liquider sans que tout soit évalué">
          <input type="number" min={0} value={valeur} onChange={(e) => setValeur(e.target.value)} />
        </Champ>
        <Champ label="Source de la valeur"
               aide="une valeur sans source ne vaut pas évaluation et ne peut pas fonder une liquidation">
          <select value={sourceValeur} onChange={(e) => setSourceValeur(e.target.value)}>
            <option value="">— non déclarée —</option>
            {SOURCES_VALEUR.map((v) => (
              <option key={v} value={v}>{LIBELLE_SOURCE_VALEUR[v]}</option>
            ))}
          </select>
        </Champ>
        <Champ label="Parcelle" aide="si l'élément est un immeuble du registre">
          <input value={parcelle} onChange={(e) => setParcelle(e.target.value)} placeholder="GN-PAR-…" />
        </Champ>
      </div>
      <div className="actions">
        <button type="submit" className="bouton" disabled={envoi || designation.trim().length < 3}>
          {envoi ? 'Inscription…' : "Inscrire à la masse"}
        </button>
      </div>
    </form>
  );
}
